import { careersApplicationFeedbackPath, careersApplicationNotePath, careersApplicationPath } from "../../utils/careersRoutes";
import { NotificationType } from "../../utils/models";
import { restoreText } from "./careersFormat";

/** Who did it, to whom, on which job — every Careers bell notification carries these. */
type CareersNotificationBase = { application_id: string; candidate_name: string; job_title: string; author_name: string };

/**
 * `notification_data` of a Careers bell notification. The bell renders its own translated line from these (the
 * row's `title` / `message` are English, for the table only). Written once, when it is sent, so names are as they
 * were then.
 *  - `note_mention` / `feedback_mention`: someone @mentioned the reader in a note / a feedback's comment;
 *    `excerpt` is its first characters (up to 140).
 *  - `feedback_reminder`: someone asked the reader, an interviewer, for their feedback on the candidate;
 *    `step_name` is the interview's step (null when it had none).
 */
export type CareersNotificationData =
	| (CareersNotificationBase & { kind: "note_mention"; note_id: string; excerpt: string })
	| (CareersNotificationBase & { kind: "feedback_mention"; feedback_id: string; excerpt: string })
	| (CareersNotificationBase & { kind: "feedback_reminder"; step_name: string | null });

/** The Careers bell types, and which kind of data each carries. */
const CAREERS_NOTIFICATION_KIND = new Map<string, CareersNotificationData["kind"]>([
	[NotificationType.CareersNoteMention, "note_mention"],
	[NotificationType.CareersFeedbackMention, "feedback_mention"],
	[NotificationType.CareersFeedbackReminder, "feedback_reminder"],
]);

/** Which kind of Careers notification a bell row is, or undefined for any other notification type. */
export const careersNotificationKind = (notificationType: string): CareersNotificationData["kind"] | undefined =>
	CAREERS_NOTIFICATION_KIND.get(notificationType);

/**
 * Read a Careers notification's data, or throw naming the first field that is missing or not text. It may arrive as
 * a JSON string or an object; hand-written fields go through `restoreText`, because the network layer turns an
 * ISO-date-looking string (a note that starts "2027-03-01") into a Luxon object.
 */
export const parseCareersNotificationData = (raw: unknown, kind: CareersNotificationData["kind"]): CareersNotificationData => {
	const data: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
	if (typeof data !== "object" || data === null) throw new Error(`careers ${kind} notification has no data`);
	const record = data as Record<string, unknown>; // An object checked just above; each field is checked below.
	const read = (field: string): string => {
		if (!(field in record)) throw new Error(`careers ${kind} notification is missing ${field}`);
		return restoreText(record[field]);
	};
	const base = {
		application_id: read("application_id"),
		candidate_name: read("candidate_name"),
		job_title: read("job_title"),
		author_name: read("author_name"),
	};
	switch (kind) {
		case "note_mention":
			return { ...base, kind, note_id: read("note_id"), excerpt: read("excerpt") };
		case "feedback_mention":
			return { ...base, kind, feedback_id: read("feedback_id"), excerpt: read("excerpt") };
		case "feedback_reminder":
			if (!("step_name" in record)) throw new Error("careers feedback_reminder notification is missing step_name");
			return { ...base, kind, step_name: record.step_name === null ? null : read("step_name") };
	}
};

/**
 * Where a Careers notification opens: the candidate's profile — with the All notes / All feedback panel on the
 * mentioning entry, or, for a reminder, on the Feedback tab (where Add feedback is).
 */
export const careersNotificationDestination = (data: CareersNotificationData): string => {
	switch (data.kind) {
		case "note_mention":
			return careersApplicationNotePath(data.application_id, data.note_id);
		case "feedback_mention":
			return careersApplicationFeedbackPath(data.application_id, data.feedback_id);
		case "feedback_reminder":
			return careersApplicationPath(data.application_id);
	}
};
